// CRM Inbox — folds the classic MailInbox / MailOutbox / MailSent screens into one
// CRM shell with three tabs. Each tab is a master-detail (rail + pane) with full
// parity to its classic source:
//   INBOX  — replies: classification/intent/persona filters, thread, reply composer
//            (ReplyEditor), mark-read, outcome, 45s poll, load-more.
//   OUTBOX — queued sends: status chips, campaign filter, queue actions
//            (pause/resume/send-now/skip-step/remove), sequence preview + projected dates.
//   SENT   — sent messages: search, campaign filter, detail with reply badge.
// Every thread/message links back to the contact record and company record.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import ReplyEditor from '../../components/ReplyEditor';

const TABS = ['Inbox', 'Outbox', 'Sent'];

function fmtWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString([], { day: 'numeric', month: 'short' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtFull(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
         d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtRel(ts) {
  if (!ts) return 'unscheduled';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = d - now;
  const absMin = Math.abs(diffMs) / 60000;
  if (absMin < 60) return diffMs > 0 ? `in ${Math.round(absMin)}m` : `${Math.round(absMin)}m ago`;
  const absHr = absMin / 60;
  if (absHr < 24) return diffMs > 0 ? `in ${Math.round(absHr)}h` : `${Math.round(absHr)}h ago`;
  return fmtWhen(ts);
}
function senderName(fromEmail) {
  if (!fromEmail) return '';
  const m = fromEmail.match(/^([^<]+?)\s*<.+>$/);
  if (m) return m[1].replace(/^"|"$/g, '').trim();
  return fromEmail.split('@')[0];
}

const INTENT_OPTIONS = [
  { value: '', label: 'Any intent' },
  { value: 'interested', label: 'Interested', cls: 'crmib-i-green' },
  { value: 'question', label: 'Question', cls: 'crmib-i-blue' },
  { value: 'referral', label: 'Referral', cls: 'crmib-i-cyan' },
  { value: 'out_of_office', label: 'Out of office', cls: 'crmib-i-amber' },
  { value: 'not_interested', label: 'Not interested', cls: 'crmib-i-rose' },
  { value: 'unsubscribe_request', label: 'Unsubscribe', cls: 'crmib-i-rose' },
  { value: 'other', label: 'Other', cls: 'crmib-i-slate' },
];
const INTENT_META = INTENT_OPTIONS.reduce((a, o) => { if (o.value) a[o.value] = o; return a; }, {});

const CLASSIFICATIONS = [
  { value: '', label: 'All' },
  { value: 'reply', label: 'Replies' },
  { value: 'auto_reply', label: 'Auto-replies' },
  { value: 'bounce', label: 'Bounces' },
];

const OUTCOMES = [
  { value: 'interested', label: 'Interested' },
  { value: 'meeting_booked', label: 'Meeting booked' },
  { value: 'won', label: 'Won' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'wrong_person', label: 'Wrong person' },
  { value: 'lost', label: 'Lost' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const OUTBOX_STATUS = {
  pending:     { label: 'Pending',     cls: 'crmib-st-blue' },
  in_progress: { label: 'In progress', cls: 'crmib-st-amber' },
  paused:      { label: 'Paused',      cls: 'crmib-st-slate' },
  failed:      { label: 'Failed',      cls: 'crmib-st-rose' },
};

export default function CrmInbox() {
  const { clientId } = useOutletContext();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Inbox');

  const goContact = useCallback((contactId) => {
    if (!contactId) return;
    navigate(`/clients/${clientId}/crm/contacts/${contactId}`, { state: { from: `/clients/${clientId}/crm/inbox` } });
  }, [clientId, navigate]);
  const goCompany = useCallback((companyId) => {
    if (!companyId) return;
    navigate(`/clients/${clientId}/crm/companies/${companyId}`);
  }, [clientId, navigate]);

  return (
    <div className="crmib-root">
      <div className="crmib-tabs">
        {TABS.map(t => (
          <button key={t} className={'crmib-tab' + (tab === t ? ' crmib-tab--on' : '')} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      <div className="crmib-panel">
        {tab === 'Inbox' && <InboxTab clientId={clientId} goContact={goContact} goCompany={goCompany} />}
        {tab === 'Outbox' && <OutboxTab clientId={clientId} goContact={goContact} goCompany={goCompany} />}
        {tab === 'Sent' && <SentTab clientId={clientId} goContact={goContact} goCompany={goCompany} />}
      </div>
    </div>
  );
}

const INBOX_PAGE = 50;
const POLL_MS = 45000;

function SendFromPicker({ mailboxes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const active = useMemo(() => mailboxes.filter(m => m.status === 'active'), [mailboxes]);
  const selected = active.find(m => m.id === value) || null;
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return active;
    return active.filter(m =>
      (m.email_address || '').toLowerCase().includes(t) ||
      (m.display_name || '').toLowerCase().includes(t)
    );
  }, [active, q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const label = (m) => m.display_name ? `${m.display_name} \u00b7 ${m.email_address}` : m.email_address;
  return (
    <div className="crmib-sf" ref={wrapRef}>
      <span className="crmib-sf-lbl">Send from</span>
      <div className="crmib-sf-ctl">
        <button type="button" className="crmib-sf-btn" onClick={() => setOpen(o => !o)}>
          <span className="crmib-sf-cur">{selected ? label(selected) : 'Select a mailbox'}</span>
          <span className="crmib-sf-caret">{open ? '\u25b4' : '\u25be'}</span>
        </button>
        {open && (
          <div className="crmib-sf-panel">
            <input
              className="crmib-sf-search"
              autoFocus
              placeholder="Search mailboxes"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <div className="crmib-sf-list">
              {filtered.length === 0 && <div className="crmib-sf-none">No matches</div>}
              {filtered.map(m => (
                <button
                  type="button"
                  key={m.id}
                  className={'crmib-sf-opt' + (m.id === value ? ' crmib-sf-opt--on' : '')}
                  onClick={() => { onChange(m.id); setOpen(false); setQ(''); }}
                >
                  <span className="crmib-sf-opt-name">{m.display_name || m.email_address}</span>
                  {m.display_name && <span className="crmib-sf-opt-email">{m.email_address}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InboxTab({ clientId, goContact, goCompany }) {
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [classification, setClassification] = useState('');
  const [intent, setIntent] = useState('');
  const [persona, setPersona] = useState('');
  const [personas, setPersonas] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [sendFromId, setSendFromId] = useState(null);
  const [query, setQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyHtml, setReplyHtml] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [outcomeSaving, setOutcomeSaving] = useState(false);

  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback((off) => {
    const p = new URLSearchParams();
    if (classification) p.set('classification', classification);
    if (intent) p.set('intent', intent);
    if (persona) p.set('persona', persona);
    if (query) p.set('q', query);
    p.set('limit', String(INBOX_PAGE));
    p.set('offset', String(off));
    return `${API_BASE}/clients/${clientId}/inbox?${p.toString()}`;
  }, [classification, intent, persona, query, clientId]);

  const fetchThread = useCallback(async (reply) => {
    setThreadLoading(true); setThreadError(null); setThread(null);
    setReplyOpen(false); setReplyHtml('');
    try {
      const token = await getToken();
      const replyRes = await fetch(`${API_BASE}/inbox/${reply.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!replyRes.ok) throw new Error(`Failed to load reply (${replyRes.status})`);
      const replyData = await replyRes.json();
      let messages = [];
      let contactMeta = null;
      if (replyData.contact_id) {
        const convRes = await fetch(`${API_BASE}/conversations/by-contact/${replyData.contact_id}?mailbox_id=${replyData.mailbox_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (convRes.ok) { const cd = await convRes.json(); messages = cd.messages || []; contactMeta = cd.contact || null; }
      }
      if (!messages.length) {
        messages = [{ id: replyData.id, direction: 'inbound', subject: replyData.subject, from_email: replyData.from_email, to_email: replyData.to_email, occurred_at: replyData.received_at, body_html: replyData.body_html, body_text: replyData.body_text }];
      }
      setThread({ reply: replyData, messages, contact: contactMeta });
      setMailboxes(mbs => {
        const active = mbs.filter(m => m.status === 'active');
        const inboundActive = active.find(m => m.id === replyData.mailbox_id);
        setSendFromId((inboundActive || active[0])?.id ?? null);
        return mbs;
      });
      if (replyData.is_read === false) {
        fetch(`${API_BASE}/inbox/${reply.id}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ is_read: true }) }).catch(() => {});
        setList(prev => prev.map(r => r.id === reply.id ? { ...r, is_read: true } : r));
      }
    } catch (e) { setThreadError(e.message); }
    finally { setThreadLoading(false); }
  }, [getToken]);

  const handleSelect = useCallback((reply) => { setSelectedId(reply.id); fetchThread(reply); }, [fetchThread]);

  const fetchList = useCallback(async ({ append = false, silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const off = append ? offset : 0;
      const res = await fetch(buildUrl(off), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load inbox (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setHasMore(rows.length >= INBOX_PAGE);
      if (append) { setList(prev => [...prev, ...rows]); setOffset(off + rows.length); }
      else {
        setList(rows); setOffset(rows.length);
        if (firstLoadRef.current && rows.length) { firstLoadRef.current = false; handleSelect(rows[0]); }
      }
    } catch (e) { if (!silent) setListError(e.message); }
    finally { if (!silent) setListLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl, getToken, offset, handleSelect]);

  useEffect(() => {
    if (!clientId) return;
    firstLoadRef.current = true;
    fetchList({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, classification, intent, persona, query]);

  useEffect(() => {
    if (!clientId) { setPersonas([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/clients/${clientId}/personas`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && !cancelled) setPersonas(await res.json());
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [clientId, getToken]);

  useEffect(() => {
    if (!clientId) { setMailboxes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && !cancelled) setMailboxes(await res.json());
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [clientId, getToken]);

  useEffect(() => {
    if (!clientId) return;
    pollRef.current = setInterval(() => fetchList({ append: false, silent: true }), POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, classification, intent, persona, query]);

  const handleSendReply = async () => {
    if (!thread || !replyHtml.replace(/<[^>]+>/g, '').trim()) return;
    if (!sendFromId) { toast.error('No active mailbox to send from'); return; }
    setReplySending(true);
    try {
      const token = await getToken();
      const r = thread.reply;
      const subject = r.subject && r.subject.toLowerCase().startsWith('re:') ? r.subject : `Re: ${r.subject || ''}`.trim();
      const fromAddr = (r.from_email || '').match(/<([^>]+)>/)?.[1] || r.from_email;
      const res = await fetch(`${API_BASE}/replies/send`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox_id: sendFromId, to: fromAddr, subject, html: replyHtml, text: replyHtml.replace(/<[^>]+>/g, ''), in_reply_to_provider_id: r.provider_message_id, contact_id: r.contact_id, campaign_id: r.campaign_id, original_reply_id: r.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Send failed (${res.status})`);
      setReplyOpen(false); setReplyHtml('');
      toast.success('Reply sent');
      fetchThread(thread.reply);
    } catch (e) { toast.error(e.message); }
    finally { setReplySending(false); }
  };

  const handleOutcome = async (outcome) => {
    if (!thread) return;
    setOutcomeSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/inbox/${thread.reply.id}/outcome`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome }) });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setThread(t => t ? { ...t, reply: { ...t.reply, outcome } } : t);
      setList(prev => prev.map(r => r.id === thread.reply.id ? { ...r, outcome } : r));
      toast.success(outcome ? 'Outcome set' : 'Outcome cleared');
    } catch (e) { toast.error('Outcome save failed: ' + e.message); }
    finally { setOutcomeSaving(false); }
  };

  const filteredList = useMemo(() => unreadOnly ? list.filter(r => r.is_read === false) : list, [list, unreadOnly]);
  const unreadCount = useMemo(() => list.filter(r => r.is_read === false).length, [list]);
  const currentOutcome = thread?.reply?.outcome || null;

  return (
    <div className="crmib-md">
      <div className="crmib-rail">
        <div className="crmib-rail-head">
          <div className="crmib-rail-titlerow">
            <span className="crmib-rail-title">Inbox {unreadCount > 0 && <span className="crmib-unread">{unreadCount}</span>}</span>
            <button className={'crmib-toggle' + (unreadOnly ? ' crmib-toggle--on' : '')} onClick={() => setUnreadOnly(v => !v)}>Unread only</button>
          </div>
          <input className="crmib-input" type="text" placeholder="Search subject, sender, body…" value={query} onChange={e => setQuery(e.target.value)} />
          <select className="crmib-select" value={classification} onChange={e => setClassification(e.target.value)}>
            {CLASSIFICATIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select className="crmib-select" value={intent} onChange={e => setIntent(e.target.value)}>
            {INTENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {personas.length > 0 && (
            <select className="crmib-select" value={persona} onChange={e => setPersona(e.target.value)}>
              <option value="">Any persona</option>
              {personas.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          )}
        </div>
        <div className="crmib-rail-list">
          {listLoading && <div className="crmib-muted">Loading…</div>}
          {listError && <div className="crmib-err">{listError}</div>}
          {!listLoading && !listError && filteredList.length === 0 && <div className="crmib-muted">No messages.</div>}
          {filteredList.map(r => {
            const sel = r.id === selectedId;
            const unread = r.is_read === false;
            const im = r.intent_classification && INTENT_META[r.intent_classification];
            return (
              <div key={r.id} onClick={() => handleSelect(r)} className={'crmib-card' + (sel ? ' crmib-card--on' : '') + (unread ? ' crmib-card--unread' : '')}>
                <div className="crmib-card-top">
                  <span className="crmib-card-from">{senderName(r.from_email)}</span>
                  <span className="crmib-card-when">{fmtWhen(r.received_at)}</span>
                </div>
                <div className="crmib-card-subj">{r.subject || '(no subject)'}</div>
                <div className="crmib-card-prev">{(r.body_preview || '').replace(/\s+/g, ' ').slice(0, 90)}</div>
                <div className="crmib-card-tags">
                  {im && <span className={'crmib-itag ' + im.cls}>{im.label}</span>}
                  {r.persona_name && <span className="crmib-ptag">{r.persona_name}</span>}
                  {r.outcome && <span className="crmib-otag">{(OUTCOMES.find(o => o.value === r.outcome) || {}).label || r.outcome}</span>}
                  {r.company_name && <span className="crmib-cotag">{r.company_name}</span>}
                </div>
              </div>
            );
          })}
          {hasMore && !listLoading && filteredList.length > 0 && (
            <div className="crmib-loadmore"><button className="crmib-btn crmib-btn--sm" onClick={() => fetchList({ append: true })}>Load more</button></div>
          )}
        </div>
      </div>

      <div className="crmib-pane">
        {!thread && !threadLoading && !threadError && <div className="crmib-pane-empty">Select a message to read.</div>}
        {threadLoading && <div className="crmib-muted crmib-pad">Loading thread…</div>}
        {threadError && <div className="crmib-err crmib-pad">{threadError}</div>}
        {thread && (
          <>
            <div className="crmib-pane-head">
              <div className="crmib-pane-head-l">
                <div className="crmib-pane-subj">{thread.reply.subject || '(no subject)'}</div>
                <div className="crmib-pane-meta">
                  {thread.reply.contact_id
                    ? <button className="crmib-link" onClick={() => goContact(thread.reply.contact_id)}>{thread.contact?.full_name || senderName(thread.reply.from_email)}</button>
                    : <span>{thread.contact?.full_name || senderName(thread.reply.from_email)}</span>}
                  {thread.contact?.job_title && <span> · {thread.contact.job_title}</span>}
                  {(thread.contact?.company_id || thread.reply.company_id)
                    ? <> · <button className="crmib-link" onClick={() => goCompany(thread.contact?.company_id || thread.reply.company_id)}>{thread.reply.company_name || thread.contact?.company_name}</button></>
                    : thread.reply.company_name && <span> · {thread.reply.company_name}</span>}
                  {thread.reply.campaign_name && <span> · Campaign: {thread.reply.campaign_name}</span>}
                </div>
              </div>
              <select className="crmib-select crmib-select--sm" value={currentOutcome || ''} disabled={outcomeSaving}
                onChange={e => { const v = e.target.value; if (v === '__clear__') return handleOutcome(null); if (v === '') return; handleOutcome(v); }}>
                <option value="" disabled hidden>Set outcome…</option>
                {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                {currentOutcome && <option value="__clear__">— Clear outcome —</option>}
              </select>
            </div>
            <div className="crmib-thread">
              {thread.messages.map((m, i) => {
                const inbound = m.direction === 'inbound';
                return (
                  <div key={`${m.direction}-${m.id}-${i}`} className={'crmib-msg crmib-msg--' + (inbound ? 'in' : 'out')}>
                    <div className="crmib-msg-head">
                      <span><strong>{inbound ? 'From' : 'Sent'}:</strong> {inbound ? m.from_email : m.to_email}</span>
                      <span>{m.occurred_at ? new Date(m.occurred_at).toLocaleString() : ''}</span>
                    </div>
                    {m.body_html
                      ? <div className="crmib-msg-body" dangerouslySetInnerHTML={{ __html: m.body_html }} />
                      : <div className="crmib-msg-body crmib-msg-body--pre">{m.body_text || ''}</div>}
                  </div>
                );
              })}
            </div>
            <div className="crmib-reply">
              {!replyOpen ? (
                <button className="crmib-btn crmib-btn--primary" onClick={() => setReplyOpen(true)}>Reply</button>
              ) : (
                <div>
                  <ReplyEditor value={replyHtml} onChange={setReplyHtml} clientId={clientId} />
                  {mailboxes.filter(m => m.status === 'active').length === 0 ? (
                    <div className="crmib-sendfrom crmib-sendfrom--empty">No active mailbox to send from. Connect or activate a mailbox in Deliverability.</div>
                  ) : (
                    <SendFromPicker mailboxes={mailboxes} value={sendFromId} onChange={setSendFromId} />
                  )}
                  <div className="crmib-reply-actions">
                    <button className="crmib-btn crmib-btn--primary" onClick={handleSendReply} disabled={replySending || !sendFromId || !replyHtml.replace(/<[^>]+>/g, '').trim()}>{replySending ? 'Sending…' : 'Send'}</button>
                    <button className="crmib-btn" onClick={() => { setReplyOpen(false); setReplyHtml(''); }} disabled={replySending}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const OUTBOX_POLL_MS = 30000;

function OutboxTab({ clientId, goContact, goCompany }) {
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [seq, setSeq] = useState(null);
  const [seqLoading, setSeqLoading] = useState(false);
  const [waitDays, setWaitDays] = useState([2, 3, 4, 5]);

  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback(() => {
    const p = new URLSearchParams();
    p.set('limit', '100');
    if (campaignFilter) p.set('campaign_id', campaignFilter);
    return `${API_BASE}/clients/${clientId}/outbox?${p.toString()}`;
  }, [campaignFilter, clientId]);

  const fetchList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const res = await fetch(buildUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load outbox (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setList(rows);
      if (firstLoadRef.current && rows.length) { firstLoadRef.current = false; setSelectedId(rows[0].id); }
    } catch (e) { if (!silent) setListError(e.message); }
    finally { if (!silent) setListLoading(false); }
  }, [buildUrl, getToken]);

  useEffect(() => {
    if (!clientId) return;
    firstLoadRef.current = true;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, campaignFilter]);

  useEffect(() => {
    if (!clientId) return;
    pollRef.current = setInterval(() => fetchList({ silent: true }), OUTBOX_POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, campaignFilter]);

  useEffect(() => {
    let cancelled = false;
    const row = list.find(r => r.id === selectedId);
    const contactId = row?.contact_id;
    if (!contactId) { setSeq(null); return; }
    setSeqLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`, { headers: { Authorization: `Bearer ${token}` } });
        const rows = res.ok ? await res.json() : [];
        if (cancelled) return;
        setSeq((Array.isArray(rows) ? rows : []).find(x => x.channel === 'email') || null);
        if (row?.campaign_id) {
          const cres = await fetch(`${API_BASE}/campaigns/${row.campaign_id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (cres.ok) { const cd = await cres.json(); if (!cancelled) setWaitDays(Array.isArray(cd.default_step_wait_days) && cd.default_step_wait_days.length ? cd.default_step_wait_days : [2, 3, 4, 5]); }
        }
      } catch (_) {}
      finally { if (!cancelled) setSeqLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedId, list, getToken]);

  const campaigns = useMemo(() => {
    const m = new Map();
    list.forEach(r => { if (r.campaign_id && r.campaign_name) m.set(r.campaign_id, r.campaign_name); });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [list]);

  const statusCounts = useMemo(() => {
    const c = { pending: 0, in_progress: 0, paused: 0, failed: 0 };
    list.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [list]);

  const filteredList = useMemo(() => {
    let out = list;
    if (statusFilter) out = out.filter(r => r.status === statusFilter);
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(r => (r.full_name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q) || (r.company_name || '').toLowerCase().includes(q) || (r.campaign_name || '').toLowerCase().includes(q));
    }
    return out;
  }, [list, query, statusFilter]);

  const selected = useMemo(() => filteredList.find(r => r.id === selectedId) || list.find(r => r.id === selectedId), [filteredList, list, selectedId]);

  const doAction = async (action) => {
    if (!selected) return;
    if (action === 'remove' && !window.confirm('Remove this contact from the campaign? This cannot be undone.')) return;
    setActionBusy(action);
    try {
      const token = await getToken();
      const method = action === 'remove' ? 'DELETE' : 'POST';
      const path = action === 'remove' ? `/campaign-contacts/${selected.id}` : `/campaign-contacts/${selected.id}/${action}`;
      const res = await fetch(`${API_BASE}${path}`, { method, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Failed (${res.status})`); }
      toast.success(action === 'remove' ? 'Removed' : `${action} applied`);
      if (action === 'remove') setSelectedId(null);
      await fetchList();
    } catch (e) { toast.error(e.message); }
    finally { setActionBusy(null); }
  };

  const projected = useMemo(() => {
    if (!selected || !seq || !Array.isArray(seq.content)) return {};
    const steps = seq.content;
    const cur = selected.current_step != null ? (selected.current_step + 1) : 1;
    const anchor = selected.next_send_at ? new Date(selected.next_send_at) : new Date();
    const hypo = !selected.next_send_at;
    const dates = { _hypothetical: hypo };
    dates[cur] = anchor;
    let cursor = new Date(anchor);
    for (let i = cur; i < steps.length; i++) {
      const sd = steps[i - 1];
      const wd = (sd && typeof sd.wait_days === 'number') ? sd.wait_days : (waitDays[i - 1] != null ? waitDays[i - 1] : 3);
      cursor = new Date(cursor.getTime() + wd * 86400000);
      dates[i + 1] = new Date(cursor);
    }
    cursor = new Date(anchor);
    for (let i = cur - 1; i >= 1; i--) {
      const sd = steps[i - 1];
      const wd = (sd && typeof sd.wait_days === 'number') ? sd.wait_days : (waitDays[i - 1] != null ? waitDays[i - 1] : 3);
      cursor = new Date(cursor.getTime() - wd * 86400000);
      dates[i] = new Date(cursor);
    }
    return dates;
  }, [selected, seq, waitDays]);

  const stepDate = (d, isPast, isCurrent) => {
    if (!d) return null;
    const opts = { day: 'numeric', month: 'short' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    const s = d.toLocaleDateString([], opts);
    if (isCurrent) return `Sends ${s}`;
    if (isPast) return `Sent ${s}`;
    return `~ ${s}`;
  };

  return (
    <div className="crmib-md">
      <div className="crmib-rail">
        <div className="crmib-rail-head">
          <div className="crmib-rail-titlerow">
            <span className="crmib-rail-title">Outbox</span>
            <button className="crmib-refresh" onClick={() => fetchList()} title="Refresh">↻</button>
          </div>
          <div className="crmib-statuschips">
            {Object.entries(OUTBOX_STATUS).map(([key, meta]) => {
              const n = statusCounts[key];
              const on = statusFilter === key;
              return <button key={key} className={'crmib-statuschip ' + meta.cls + (on ? ' crmib-statuschip--on' : '')} onClick={() => setStatusFilter(on ? '' : key)}>{meta.label}{n > 0 && ` · ${n}`}</button>;
            })}
          </div>
          <input className="crmib-input" type="text" placeholder="Search contact, email, company…" value={query} onChange={e => setQuery(e.target.value)} />
          <select className="crmib-select" value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}>
            <option value="">All campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="crmib-rail-list">
          {listLoading && list.length === 0 && <div className="crmib-muted">Loading…</div>}
          {listError && <div className="crmib-err">{listError}</div>}
          {!listLoading && filteredList.length === 0 && <div className="crmib-muted">Nothing in the outbox. Contacts queued for sending will appear here.</div>}
          {filteredList.map(r => {
            const sel = r.id === selectedId;
            const meta = OUTBOX_STATUS[r.status] || { label: r.status, cls: 'crmib-st-slate' };
            return (
              <div key={r.id} onClick={() => setSelectedId(r.id)} className={'crmib-card' + (sel ? ' crmib-card--on' : '')}>
                <div className="crmib-card-top">
                  <span className="crmib-card-from">{r.full_name || r.email}</span>
                  <span className="crmib-card-when">{fmtRel(r.next_send_at)}</span>
                </div>
                <div className="crmib-card-subj">{r.company_name || r.email}</div>
                <div className="crmib-card-tags">
                  <span className={'crmib-st ' + meta.cls}>{meta.label}</span>
                  {r.campaign_name && <span className="crmib-cotag">{r.campaign_name}{r.current_step != null ? ` · step ${r.current_step + 1}` : ''}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="crmib-pane">
        {!selected && <div className="crmib-pane-empty">Select a queued contact to view.</div>}
        {selected && (() => {
          const meta = OUTBOX_STATUS[selected.status] || { label: selected.status, cls: 'crmib-st-slate' };
          return (
            <>
              <div className="crmib-pane-head">
                <div className="crmib-pane-head-l">
                  <div className="crmib-pane-subj">{selected.full_name || selected.email}</div>
                  <div className="crmib-pane-meta">
                    <span>{selected.email}</span>
                    {selected.job_title && <span> · {selected.job_title}</span>}
                    {selected.company_id
                      ? <> · <button className="crmib-link" onClick={() => goCompany(selected.company_id)}>{selected.company_name}</button></>
                      : selected.company_name && <span> · {selected.company_name}</span>}
                    {selected.contact_id && <> · <button className="crmib-link" onClick={() => goContact(selected.contact_id)}>Open contact →</button></>}
                  </div>
                </div>
                <span className={'crmib-st ' + meta.cls}>{meta.label}</span>
              </div>
              <div className="crmib-outbox-body">
                <div className="crmib-actions">
                  {selected.status === 'paused' && <button className="crmib-btn crmib-btn--primary" disabled={actionBusy === 'resume'} onClick={() => doAction('resume')}>{actionBusy === 'resume' ? 'Resuming…' : 'Resume'}</button>}
                  {(selected.status === 'pending' || selected.status === 'in_progress') && <button className="crmib-btn" disabled={actionBusy === 'pause'} onClick={() => doAction('pause')}>{actionBusy === 'pause' ? 'Pausing…' : 'Pause'}</button>}
                  <button className="crmib-btn crmib-btn--primary" disabled={actionBusy === 'send-now'} onClick={() => doAction('send-now')}>{actionBusy === 'send-now' ? 'Sending…' : 'Send now'}</button>
                  <button className="crmib-btn" disabled={actionBusy === 'skip-step'} onClick={() => doAction('skip-step')}>{actionBusy === 'skip-step' ? 'Skipping…' : 'Skip step'}</button>
                  <button className="crmib-btn crmib-btn--danger" disabled={actionBusy === 'remove'} onClick={() => doAction('remove')}>{actionBusy === 'remove' ? 'Removing…' : 'Remove from campaign'}</button>
                </div>
                <div className="crmib-detailgrid">
                  <div className="crmib-dg-k">Campaign</div><div className="crmib-dg-v">{selected.campaign_name || '—'}</div>
                  <div className="crmib-dg-k">Current step</div><div className="crmib-dg-v">{selected.current_step != null ? `Step ${selected.current_step + 1}` : '—'}</div>
                  <div className="crmib-dg-k">Next send</div><div className="crmib-dg-v">{fmtFull(selected.next_send_at)} {selected.next_send_at && <span className="crmib-faint">({fmtRel(selected.next_send_at)})</span>}</div>
                  <div className="crmib-dg-k">Status</div><div className="crmib-dg-v"><span className={'crmib-st ' + meta.cls}>{meta.label}</span></div>
                  {selected.last_event && <><div className="crmib-dg-k">Last event</div><div className="crmib-dg-v">{selected.last_event}{selected.last_event_at ? ` · ${fmtRel(selected.last_event_at)}` : ''}</div></>}
                </div>
                <div className="crmib-preview">
                  <div className="crmib-preview-head"><span>Email preview</span><span className="crmib-faint">Read-only. Edit on the Sequences screen.</span></div>
                  {seqLoading && <div className="crmib-muted crmib-pad">Loading sequence…</div>}
                  {!seqLoading && (!seq || !Array.isArray(seq.content) || seq.content.length === 0) && <div className="crmib-muted crmib-pad">No email sequence found for this contact.</div>}
                  {!seqLoading && seq && Array.isArray(seq.content) && seq.content.length > 0 && seq.content.map((step, idx) => {
                    const stepNum = step.step != null ? step.step : (idx + 1);
                    const isCurrent = selected.current_step != null && stepNum === (selected.current_step + 1);
                    const isPast = selected.current_step != null && stepNum < (selected.current_step + 1);
                    const hypo = projected._hypothetical;
                    let label = stepDate(projected[stepNum], isPast, isCurrent);
                    if (hypo && label && !isPast) label = 'if resumed: ' + label.replace(/^(Sends|~ )/, '').trim();
                    return (
                      <div key={idx} className={'crmib-prevstep' + (isCurrent ? ' crmib-prevstep--on' : '')}>
                        <div className="crmib-prevstep-head">
                          <span className="crmib-prevstep-n">Step {stepNum}{isCurrent && ' · next to send'}</span>
                          {label && <span className="crmib-prevstep-date">· {label}</span>}
                          {seq.approval_status && <span className={'crmib-st ' + (seq.approval_status === 'approved' ? 'crmib-st-green' : 'crmib-st-amber')}>{seq.approval_status}</span>}
                        </div>
                        {step.subject && <div className="crmib-prevstep-subj">{step.subject}</div>}
                        {step.body && <div className="crmib-prevstep-body">{step.body}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

const SENT_PAGE = 50;
const SENT_POLL_MS = 60000;

function SentTab({ clientId, goContact, goCompany }) {
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback((off) => {
    const p = new URLSearchParams();
    p.set('limit', String(SENT_PAGE));
    p.set('offset', String(off));
    if (query) p.set('q', query);
    if (campaignFilter) p.set('campaign_id', campaignFilter);
    return `${API_BASE}/clients/${clientId}/sent?${p.toString()}`;
  }, [query, campaignFilter, clientId]);

  const fetchList = useCallback(async ({ append = false, silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const off = append ? offset : 0;
      const res = await fetch(buildUrl(off), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load sent (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setHasMore(rows.length >= SENT_PAGE);
      if (append) { setList(prev => [...prev, ...rows]); setOffset(off + rows.length); }
      else {
        setList(rows); setOffset(rows.length);
        if (firstLoadRef.current && rows.length) { firstLoadRef.current = false; setSelectedId(rows[0].id); }
      }
    } catch (e) { if (!silent) setListError(e.message); }
    finally { if (!silent) setListLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl, getToken, offset]);

  useEffect(() => {
    if (!clientId) return;
    firstLoadRef.current = true;
    fetchList({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, query, campaignFilter]);

  useEffect(() => {
    if (!clientId) return;
    pollRef.current = setInterval(() => fetchList({ append: false, silent: true }), SENT_POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, query, campaignFilter]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true); setDetailError(null); setDetail(null);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/sent/${selectedId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`Failed to load message (${res.status})`);
        const data = await res.json();
        if (!cancelled) setDetail(data);
      } catch (e) { if (!cancelled) setDetailError(e.message); }
      finally { if (!cancelled) setDetailLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedId, getToken]);

  const campaigns = useMemo(() => {
    const m = new Map();
    list.forEach(r => { if (r.campaign_id && r.campaign_name) m.set(r.campaign_id, r.campaign_name); });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [list]);

  return (
    <div className="crmib-md">
      <div className="crmib-rail">
        <div className="crmib-rail-head">
          <div className="crmib-rail-titlerow">
            <span className="crmib-rail-title">Sent</span>
            <button className="crmib-refresh" onClick={() => fetchList({ append: false })} title="Refresh">↻</button>
          </div>
          <input className="crmib-input" type="text" placeholder="Search subject, recipient, company…" value={query} onChange={e => setQuery(e.target.value)} />
          <select className="crmib-select" value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}>
            <option value="">All campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="crmib-rail-list">
          {listLoading && list.length === 0 && <div className="crmib-muted">Loading…</div>}
          {listError && <div className="crmib-err">{listError}</div>}
          {!listLoading && list.length === 0 && <div className="crmib-muted">No sent messages yet.</div>}
          {list.map(r => {
            const sel = r.id === selectedId;
            return (
              <div key={r.id} onClick={() => setSelectedId(r.id)} className={'crmib-card' + (sel ? ' crmib-card--on' : '')}>
                <div className="crmib-card-top">
                  <span className="crmib-card-from">{r.contact_name || r.to_email}</span>
                  <span className="crmib-card-when">{fmtWhen(r.sent_at)}</span>
                </div>
                <div className="crmib-card-subj">{r.subject || '(no subject)'}</div>
                <div className="crmib-card-tags">
                  {r.has_reply && <span className="crmib-st crmib-st-green">Replied</span>}
                  {r.clicked_at && <span className="crmib-st crmib-st-blue">Clicked{r.click_count > 1 ? ` ${r.click_count}×` : ''}</span>}
                  {r.opened_at && <span className="crmib-st crmib-st-green">Opened{r.open_count > 1 ? ` ${r.open_count}×` : ''}</span>}
                  {r.campaign_name && <span className="crmib-cotag">{r.campaign_name}{r.step_number ? ` · step ${r.step_number}` : ''}</span>}
                  {r.company_name && <span className="crmib-cotag">{r.company_name}</span>}
                </div>
              </div>
            );
          })}
          {hasMore && list.length >= SENT_PAGE && (
            <div className="crmib-loadmore"><button className="crmib-btn crmib-btn--sm" onClick={() => fetchList({ append: true })}>Load more</button></div>
          )}
        </div>
      </div>

      <div className="crmib-pane">
        {!selectedId && <div className="crmib-pane-empty">Select a message to view.</div>}
        {selectedId && detailLoading && <div className="crmib-muted crmib-pad">Loading…</div>}
        {selectedId && detailError && <div className="crmib-err crmib-pad">{detailError}</div>}
        {selectedId && detail && (
          <>
            <div className="crmib-pane-head">
              <div className="crmib-pane-head-l">
                <div className="crmib-pane-subj">{detail.subject || '(no subject)'}</div>
                <div className="crmib-pane-meta">
                  <span><strong>To:</strong> </span>
                  {detail.contact_id
                    ? <button className="crmib-link" onClick={() => goContact(detail.contact_id)}>{detail.contact_name || detail.to_email}</button>
                    : <span>{detail.contact_name ? `${detail.contact_name} <${detail.to_email}>` : detail.to_email}</span>}
                  {detail.company_id
                    ? <> · <button className="crmib-link" onClick={() => goCompany(detail.company_id)}>{detail.company_name}</button></>
                    : detail.company_name && <span> · {detail.company_name}</span>}
                  {detail.campaign_name && <span> · {detail.campaign_name}{detail.step_number ? ` · step ${detail.step_number}` : ''}</span>}
                </div>
                <div className="crmib-pane-meta crmib-faint">
                  <span>From: {detail.from_email}{detail.mailbox_email && detail.mailbox_email !== detail.from_email ? ` (via ${detail.mailbox_email})` : ''}</span>
                  <span> · Sent: {fmtFull(detail.sent_at)}</span>
                  {detail.has_reply && <span className="crmib-replied"> · Reply received</span>}
                  {detail.opened_at && <span> · Opened {detail.open_count > 1 ? `${detail.open_count}×, last ${fmtFull(detail.last_opened_at || detail.opened_at)}` : fmtFull(detail.opened_at)}</span>}
                  {detail.clicked_at && <span> · Clicked {fmtFull(detail.last_clicked_at || detail.clicked_at)}{detail.last_clicked_url ? ` (${detail.last_clicked_url.replace(/^https?:\/\//, '').slice(0, 40)})` : ''}</span>}
                  {!detail.opened_at && !detail.has_reply && <span> · Not opened yet</span>}
                </div>
              </div>
            </div>
            <div className="crmib-thread">
              <div className="crmib-msg crmib-msg--out">
                {detail.body_html
                  ? <div className="crmib-msg-body" dangerouslySetInnerHTML={{ __html: detail.body_html }} />
                  : <div className="crmib-msg-body crmib-msg-body--pre">{detail.body_text || '(empty)'}</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
